import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { axiosInstance } from '../../utils/axiosInstance';
import {
  Card,
  Chips,
  DownloadButton,
  EmptyState,
  ErrorBanner,
  LoadingPanel,
  PageHeader,
  PHONE_QUERY,
  RefreshButton,
  Segmented,
  Stat,
  TOUCH_QUERY,
  useMediaQuery,
} from '../ui';
import StatusMachineGrid, { machineSnapshot } from './StatusMachineGrid';
import StatusTimelineChart from './StatusTimelineChart';
import StatusTimelineGatewayCards from './StatusTimelineGatewayCards';
import StatusTimelineCleanerCard from './StatusTimelineCleanerCard';
import StatusTimelineEngineerCard from './StatusTimelineEngineerCard';
import { Location } from '../../interfaces/interfaces';
import {
  TIMELINE_PRESETS,
  STATUS_FILTER_CHIPS,
  TimelineMachine,
  TimelineSegment,
  TimelineGateway,
  MachineTypeFilter,
  StatusCategoryFilter,
  compareMachineNames,
  computeTimelineSummary,
  currentSegmentForRow,
  dedupeMachines,
  downloadCsv,
  filterTimelineRows,
  formatDuration,
  fromDatetimeLocalValue,
  getTimelineStatusCategory,
  isRowCurrentlyOutOfOrder,
  segmentsToCsv,
  toDatetimeLocalValue,
} from './statusTimelineUtils';
import './statusTimeline.scss';

export type StatusTimelineHandle = {
  /** Re-fetch the current window immediately (e.g. after an out-of-order change). */
  refresh: () => void;
};

export type StartAllMachine = {
  macId: string;
  name: string;
};

type Props = {
  siteId: string;
  locationOptions: Location[];
  locationsLoading: boolean;
  locationsError: string | null;
  onLocationChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  isConnected: boolean;
  onRetryConnect: () => void;
  onRowContextMenu?: (
    target: { macId: string; name: string; isOutOfOrder: boolean; statusLabel: string },
    x: number,
    y: number
  ) => void;
  /** Administrator-only: show Start all control. */
  showStartAll?: boolean;
  startAllBusy?: boolean;
  startAllProgressLabel?: string | null;
  onStartAllRequest?: (machines: StartAllMachine[]) => void;
};

type StatusView = 'machines' | 'timeline';

const DEFAULT_PRESET_MS = 60 * 60 * 1000;
const AUTO_REFRESH_MS = 30_000;

const VIEW_OPTIONS: { id: StatusView; label: string }[] = [
  { id: 'machines', label: 'Machines' },
  { id: 'timeline', label: 'Timeline' },
];

const TYPE_OPTIONS: { id: MachineTypeFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'washers', label: 'Washers' },
  { id: 'dryers', label: 'Dryers' },
];

const PRESET_OPTIONS: { id: number; label: string }[] = TIMELINE_PRESETS.map((p) => ({
  id: p.ms,
  label: p.label,
}));

const StatusTimeline = forwardRef<StatusTimelineHandle, Props>(({
  siteId,
  locationOptions,
  locationsLoading,
  locationsError,
  onLocationChange,
  isConnected,
  onRetryConnect,
  onRowContextMenu,
  showStartAll,
  startAllBusy,
  startAllProgressLabel,
  onStartAllRequest,
}, ref) => {
  const [presetMs, setPresetMs] = useState(DEFAULT_PRESET_MS);
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [customStart, setCustomStart] = useState(() =>
    toDatetimeLocalValue(Date.now() - DEFAULT_PRESET_MS)
  );
  const [customEnd, setCustomEnd] = useState(() => toDatetimeLocalValue(Date.now()));
  const [machines, setMachines] = useState<TimelineMachine[]>([]);
  const [segments, setSegments] = useState<TimelineSegment[]>([]);
  const [gateways, setGateways] = useState<TimelineGateway[]>([]);
  const [windowStartMs, setWindowStartMs] = useState(() => Date.now() - DEFAULT_PRESET_MS);
  const [windowEndMs, setWindowEndMs] = useState(() => Date.now());
  const [viewStartMs, setViewStartMs] = useState(() => Date.now() - DEFAULT_PRESET_MS);
  const [viewEndMs, setViewEndMs] = useState(() => Date.now());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusCategoryFilter>('all');
  const [typeFilter, setTypeFilter] = useState<MachineTypeFilter>('all');
  const [followLive, setFollowLive] = useState(true);
  // Phones open on the card grid: a timeline is the wrong shape for 390px.
  const [view, setView] = useState<StatusView>(() =>
    window.matchMedia(PHONE_QUERY).matches ? 'machines' : 'timeline'
  );
  const [showCustomRange, setShowCustomRange] = useState(false);
  const isTouch = useMediaQuery(TOUCH_QUERY);
  const fetchGen = useRef(0);
  const viewSpanRef = useRef(DEFAULT_PRESET_MS);

  const isLiveWindow = !useCustomRange || Math.abs(windowEndMs - Date.now()) < 60_000;

  const locationId = useMemo(() => {
    const match = locationOptions.find(
      (o) => o.layout_site_id != null && String(o.layout_site_id) === siteId
    );
    return match ? Number(match.locationId) : null;
  }, [locationOptions, siteId]);

  const fetchRange = useCallback(
    async (start: number, end: number, preserveZoom: boolean) => {
      if (!siteId) return;
      const gen = ++fetchGen.current;
      setLoading(true);
      setError(null);
      try {
        const { data } = await axiosInstance.post('/health/getStatusTimeline', {
          siteId,
          startDate: new Date(start).toISOString(),
          endDate: new Date(end).toISOString(),
        });
        if (gen !== fetchGen.current) return;
        setMachines(dedupeMachines(data.machines || []));
        setSegments(data.segments || []);
        setGateways(data.gateways || []);
        setWindowStartMs(start);
        setWindowEndMs(end);
        if (preserveZoom) {
          const span = viewSpanRef.current;
          let vs = Math.max(start, end - span);
          let ve = vs + span;
          if (ve > end) {
            ve = end;
            vs = Math.max(start, ve - span);
          }
          setViewStartMs(vs);
          setViewEndMs(ve);
        } else {
          setViewStartMs(start);
          setViewEndMs(end);
          viewSpanRef.current = end - start;
        }
      } catch (err: any) {
        if (gen !== fetchGen.current) return;
        console.error('Failed to load status timeline:', err);
        setError(err?.response?.data?.error || err?.message || 'Failed to load timeline');
        setMachines([]);
        setSegments([]);
        setGateways([]);
      } finally {
        if (gen === fetchGen.current) setLoading(false);
      }
    },
    [siteId]
  );

  const fetchPreset = useCallback(
    (lookbackMs: number, preserveZoom = false) => {
      const end = Date.now();
      return fetchRange(end - lookbackMs, end, preserveZoom);
    },
    [fetchRange]
  );

  useEffect(() => {
    if (useCustomRange) return;
    fetchPreset(presetMs, false);
  }, [siteId, presetMs, useCustomRange, fetchPreset]);

  // Auto-refresh every 30s while viewing a live window (independent of any socket monitoring)
  useEffect(() => {
    if (!isLiveWindow || useCustomRange) return;
    const id = setInterval(() => {
      fetchPreset(presetMs, true);
    }, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [isLiveWindow, useCustomRange, presetMs, fetchPreset]);

  // Follow live: slide view end to now
  useEffect(() => {
    if (!followLive || !isLiveWindow || useCustomRange) return;
    const id = setInterval(() => {
      const now = Date.now();
      const span = viewSpanRef.current;
      setWindowEndMs((prev) => Math.max(prev, now));
      setViewEndMs(now);
      setViewStartMs(Math.max(windowStartMs, now - span));
    }, 1000);
    return () => clearInterval(id);
  }, [followLive, isLiveWindow, useCustomRange, windowStartMs]);

  const refresh = useCallback(() => {
    if (useCustomRange) {
      const start = fromDatetimeLocalValue(customStart);
      const end = fromDatetimeLocalValue(customEnd);
      if (start == null || end == null || end <= start) return;
      fetchRange(start, end, true);
    } else {
      fetchPreset(presetMs, true);
    }
  }, [useCustomRange, customStart, customEnd, fetchRange, presetMs, fetchPreset]);

  useImperativeHandle(ref, () => ({ refresh }), [refresh]);

  const onViewChange = useCallback((startMs: number, endMs: number) => {
    viewSpanRef.current = endMs - startMs;
    setViewStartMs(startMs);
    setViewEndMs(endMs);
    if (followLive && Math.abs(endMs - Date.now()) > 5000) {
      setFollowLive(false);
    }
  }, [followLive]);

  const applyCustomRange = () => {
    const start = fromDatetimeLocalValue(customStart);
    const end = fromDatetimeLocalValue(customEnd);
    if (start == null || end == null || end <= start) {
      setError('Invalid custom range');
      return;
    }
    setUseCustomRange(true);
    setFollowLive(false);
    fetchRange(start, end, false);
  };

  const allRows = useMemo(() => {
    const byName = new Map<string, TimelineMachine>();
    dedupeMachines(machines).forEach((m) => byName.set(m.name, m));
    segments.forEach((s) => {
      const n = (s.Machine || '').trim();
      if (!n) return;
      if (!byName.has(n)) {
        byName.set(n, { mac_id: s.mac_id || '', name: n });
      } else if (!byName.get(n)!.mac_id && s.mac_id) {
        byName.set(n, { mac_id: s.mac_id, name: n });
      }
    });
    return Array.from(byName.values()).sort((a, b) => compareMachineNames(a.name, b.name));
  }, [machines, segments]);

  const filteredRows = useMemo(
    () =>
      filterTimelineRows(allRows, segments, {
        search,
        statusFilter,
        typeFilter,
        windowStartMs,
        windowEndMs,
      }),
    [allRows, segments, search, statusFilter, typeFilter, windowStartMs, windowEndMs]
  );

  // The timeline filters on anything that happened in the window. The card grid
  // shows the present, so there a status chip means "is in this state right now".
  const visibleRows = useMemo(() => {
    if (view !== 'machines' || statusFilter === 'all') return filteredRows;
    return filterTimelineRows(allRows, segments, {
      search,
      statusFilter: 'all',
      typeFilter,
      windowStartMs,
      windowEndMs,
    }).filter((row) => {
      const seg = currentSegmentForRow(row, segments);
      return !!seg && getTimelineStatusCategory(seg.statusName) === statusFilter;
    });
  }, [view, statusFilter, filteredRows, allRows, segments, search, typeFilter, windowStartMs, windowEndMs]);

  const summary = useMemo(
    () => computeTimelineSummary(visibleRows, segments, windowStartMs, windowEndMs),
    [visibleRows, segments, windowStartMs, windowEndMs]
  );

  const handleRowContextMenu = useCallback(
    (row: TimelineMachine, x: number, y: number) => {
      if (!onRowContextMenu) return;
      onRowContextMenu(
        {
          macId: row.mac_id,
          name: row.name,
          isOutOfOrder: isRowCurrentlyOutOfOrder(row, segments),
          statusLabel: machineSnapshot(row, segments).label,
        },
        x,
        y
      );
    },
    [onRowContextMenu, segments]
  );

  const exportCsv = () => {
    const names = new Set(visibleRows.map((r) => r.name));
    const csv = segmentsToCsv(segments, windowStartMs, windowEndMs, names, visibleRows);
    downloadCsv(`status-timeline-${siteId}-${Date.now()}.csv`, csv);
  };

  const handleStartAllClick = () => {
    if (!onStartAllRequest || startAllBusy) return;
    const targets: StartAllMachine[] = allRows
      .filter((m) => !!m.mac_id)
      .map((m) => ({ macId: m.mac_id, name: m.name }));
    onStartAllRequest(targets);
  };

  const presetId = useCustomRange ? null : presetMs;
  const refreshNow = () =>
    useCustomRange ? applyCustomRange() : fetchPreset(presetMs, true);

  return (
    <>
      <PageHeader
        title="Status"
        subtitle={
          onRowContextMenu && siteId
            ? isTouch
              ? 'Tap a machine for actions'
              : 'Click or right-click a machine for actions'
            : undefined
        }
        actions={
          <>
            <div
              className={`sq-live ${isConnected ? 'is-live' : 'is-offline'}`}
              title={isConnected ? 'Live connection active' : 'Reconnecting…'}
            >
              <i aria-hidden="true" />
              {isConnected ? (
                <span>Live</span>
              ) : (
                <button type="button" onClick={onRetryConnect}>
                  Offline — retry
                </button>
              )}
            </div>
            {siteId ? (
              <>
                <RefreshButton onClick={refreshNow} loading={loading} />
                <DownloadButton onClick={exportCsv} label="CSV" />
              </>
            ) : null}
            {siteId && showStartAll ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={handleStartAllClick}
                disabled={startAllBusy || loading || allRows.length === 0}
                title="Start all machines at this site"
              >
                {startAllBusy ? startAllProgressLabel || 'Starting…' : 'Start all'}
              </button>
            ) : null}
          </>
        }
      />

      <div className="sq-stack">
        <div className="sq-status-site">
          <label className="sq-field is-grow">
            <span>Location</span>
            <select
              id="status-location-select"
              className="sq-input"
              value={siteId}
              onChange={onLocationChange}
              disabled={locationsLoading || locationOptions.length === 0}
            >
              {locationsLoading ? (
                <option value="">Loading locations…</option>
              ) : locationsError ? (
                <option value="">Error loading locations</option>
              ) : locationOptions.length === 0 ? (
                <option value="">No locations available</option>
              ) : (
                locationOptions.map((option: Location) => {
                  const optionSiteId =
                    option.layout_site_id != null ? String(option.layout_site_id) : '';
                  return (
                    <option key={option.locationId} value={optionSiteId}>
                      {option.name} ({option.locationPin})
                    </option>
                  );
                })
              )}
            </select>
          </label>
          {siteId ? (
            <div className="sq-context-cards">
              <StatusTimelineCleanerCard locationId={locationId} />
              <StatusTimelineEngineerCard locationId={locationId} />
              <StatusTimelineGatewayCards gateways={gateways} />
            </div>
          ) : null}
        </div>

        {locationsError ? <ErrorBanner>{locationsError}</ErrorBanner> : null}

        {!siteId ? (
          <Card>
            <EmptyState title="Select a location to view its machines" />
          </Card>
        ) : (
          <>
            <section className="sq-stats is-compact sq-status-summary" aria-label="Summary">
              <Stat label="Available" value={`${summary.availablePct}%`} />
              <Stat label="In use" value={`${summary.inUsePct}%`} />
              <Stat label="Error / offline" value={formatDuration(summary.errorOfflineMs)} />
              <Stat
                label="In error now"
                value={summary.machinesInErrorNow}
                tone={summary.machinesInErrorNow > 0 ? 'bad' : undefined}
              />
              <Stat label="Machines" value={filteredRows.length} />
            </section>

            <Card flush>
              <div className="sq-status-controls">
                <div className="sq-status-controls-row">
                  <div className="sq-status-view">
                    <Segmented
                      block
                      label="View"
                      value={view}
                      onChange={setView}
                      options={VIEW_OPTIONS}
                    />
                  </div>
                  <Segmented
                    label="Machine type"
                    value={typeFilter}
                    onChange={setTypeFilter}
                    options={TYPE_OPTIONS}
                  />
                  <input
                    type="search"
                    className="sq-input sq-status-search"
                    placeholder="Search"
                    aria-label="Search machines"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <Chips
                  label="Filter by status"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={STATUS_FILTER_CHIPS}
                />

                <div
                  className={`sq-status-controls-row sq-status-window${
                    view === 'machines' && !showCustomRange ? ' is-machines' : ''
                  }`}
                >
                  <Segmented
                    label="Time window"
                    value={presetId}
                    onChange={(ms) => {
                      setUseCustomRange(false);
                      setShowCustomRange(false);
                      setFollowLive(true);
                      setPresetMs(ms);
                    }}
                    options={PRESET_OPTIONS}
                  />
                  <button
                    type="button"
                    className={`sq-chip${useCustomRange || showCustomRange ? ' active' : ''}`}
                    aria-expanded={showCustomRange}
                    onClick={() => setShowCustomRange((v) => !v)}
                  >
                    Custom range
                  </button>
                  {view === 'timeline' ? (
                    <label className={`sq-check${!isLiveWindow ? ' is-disabled' : ''}`}>
                      <input
                        type="checkbox"
                        checked={followLive && isLiveWindow}
                        disabled={!isLiveWindow}
                        onChange={(e) => setFollowLive(e.target.checked)}
                      />
                      Follow live
                    </label>
                  ) : null}
                </div>

                {showCustomRange ? (
                  <div className="sq-toolbar is-plain">
                    <label className="sq-field">
                      <span>From</span>
                      <input
                        type="datetime-local"
                        className="sq-input"
                        value={customStart}
                        onChange={(e) => setCustomStart(e.target.value)}
                      />
                    </label>
                    <label className="sq-field">
                      <span>To</span>
                      <input
                        type="datetime-local"
                        className="sq-input"
                        value={customEnd}
                        onChange={(e) => setCustomEnd(e.target.value)}
                      />
                    </label>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={applyCustomRange}
                    >
                      Apply range
                    </button>
                    {useCustomRange ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-link"
                        onClick={() => {
                          setUseCustomRange(false);
                          setShowCustomRange(false);
                          setFollowLive(true);
                          fetchPreset(presetMs, false);
                        }}
                      >
                        Back to presets
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {loading && machines.length === 0 ? (
                <LoadingPanel label="Loading machines" rows={6} />
              ) : error ? (
                <div className="sq-card-body">
                  <ErrorBanner onRetry={refreshNow}>{error}</ErrorBanner>
                </div>
              ) : visibleRows.length === 0 ? (
                <EmptyState title="No machines match the current filters" />
              ) : view === 'machines' ? (
                <StatusMachineGrid
                  machines={visibleRows}
                  segments={segments}
                  onSelect={onRowContextMenu ? handleRowContextMenu : undefined}
                />
              ) : (
                <StatusTimelineChart
                  machines={visibleRows}
                  segments={segments}
                  viewStartMs={viewStartMs}
                  viewEndMs={viewEndMs}
                  onViewChange={onViewChange}
                  windowStartMs={windowStartMs}
                  windowEndMs={windowEndMs}
                  onRowContextMenu={handleRowContextMenu}
                />
              )}
            </Card>
          </>
        )}
      </div>
    </>
  );
});

StatusTimeline.displayName = 'StatusTimeline';

export default StatusTimeline;
